import React, { Component } from 'react';
import { func, bool } from 'prop-types';
import { connect } from 'react-redux';
import { findIndex, range } from 'lodash-es';
import { Translate } from 'react-localize-redux';
import {
  questionTypeValues,
  SurveySchemaPartEditorType,
  SurveySchemaPartItemEditorType
} from '../../../../constants/types';
import { updateSchemaPartItemAction, updateSurveySchemaPartOptionsAction } from '../../../../actions';
import editorStyles from '../../editor.module.css';
import itemsStyles from '../SurveyPartItems/surveyPartItems.module.css';
import styles from './questionGroup.module.css';
import { AVAILABLE_LANGUAGES } from '../../../../constants';
import {
  getTranslation,
  withLang,
  langType,
  getMessages
} from '../../../../i18n';
import { assignArrayItemInObject } from '../../../../util';

const SCALE_MIN_START_VALUE = 0;
const SCALE_MAX_START_VALUE = 1;
const SCALE_DEFAULT_START_VALUE = SCALE_MAX_START_VALUE;
const DEFAULT_LIKERT_SCALE = 5;
const CUSTOM_LIKERT_SCALE = 'custom';

const getDefaultOptionTexts = (value, scale) => getMessages(`likert${scale}DefaultOptions.${value}`);

const buildResponseOptions = (scale, applyValues) =>
  Array(scale).fill({}).map((ro, idx) => ({
    value: idx + SCALE_DEFAULT_START_VALUE,
    text: applyValues ? getDefaultOptionTexts(idx + 1, scale) : {}
  }));

class QuestionGroup extends Component {
  constructor(props) {
    super(props);

    this.onChangeResponseOptionText = this.onChangeResponseOptionText.bind(this);
    this.onLikertScaleChange = this.onLikertScaleChange.bind(this);
    this.onLikertScaleStartValueChange = this.onLikertScaleStartValueChange.bind(this);
    this.onClickAddValue = this.onClickAddValue.bind(this);
    this.onClickEditValue = this.onClickEditValue.bind(this);
    this.onClickRemoveValue = this.onClickRemoveValue.bind(this);
    this.onClickAddQuestion = this.onClickAddQuestion.bind(this);
    this.onClickEditQuestion = this.onClickEditQuestion.bind(this);
    this.onChangeQuestionText = this.onChangeQuestionText.bind(this);
    this.onChangeQuestionProperty = this.onChangeQuestionProperty.bind(this);
    this.onRemoveQuestion = this.onRemoveQuestion.bind(this);
    this.setLikertScale = this.setLikertScale.bind(this);
    this.renderLocalizedTextInputs = this.renderLocalizedTextInputs.bind(this);
    this.renderActions = this.renderActions.bind(this);
    this.renderResponseOption = this.renderResponseOption.bind(this);
    this.renderQuestion = this.renderQuestion.bind(this);

    const { questionGroup } = this.props.item;

    const likertScale = (optionCount = DEFAULT_LIKERT_SCALE) => {
      if (questionGroup.responseOptions.length
        && questionGroup.responseOptions[0].value !== SCALE_DEFAULT_START_VALUE) {
        return CUSTOM_LIKERT_SCALE;
      }
      if (optionCount >= 5 && optionCount <= 7) {
        return optionCount;
      }
      return CUSTOM_LIKERT_SCALE;
    };

    this.state = {
      itemIndex: this.props.item.order - 1,
      scale: likertScale(questionGroup.responseOptions && questionGroup.responseOptions.length
        ? questionGroup.responseOptions.length : DEFAULT_LIKERT_SCALE),
      openQuestionIndex: Math.max(questionGroup.questions.length - 1, 0),
      openValueIndex: questionGroup.responseOptions
        ? Math.max(questionGroup.responseOptions.length - 1, 0) : 0,
      scaleStartValue: questionGroup.responseOptions && questionGroup.responseOptions.length
        ? questionGroup.responseOptions[0].value : SCALE_DEFAULT_START_VALUE
    };
  }

  componentDidMount() {
    if (this.isNewItem()) {
      this.setLikertScale(this.state.scale);
    }
  }

  onChangeResponseOptionText(index) {
    return (lang) => (event) => {
      const responseOptions = assignArrayItemInObject(this.props.item.questionGroup.responseOptions,
        index, { text: { [lang]: event.target.value } });
      this.props.dispatchUpdateItem(this.state.itemIndex, {
        questionGroup: { responseOptions }
      });
    };
  }

  onLikertScaleChange(event) {
    this.setLikertScale(event.target.value);
  }

  onLikertScaleStartValueChange(event) {
    const { responseOptions } = this.props.item.questionGroup;
    const scaleStartValue = Number(event.target.value);

    this.setState((prevState) => ({
      scaleStartValue,
      scale: scaleStartValue !== DEFAULT_LIKERT_SCALE ? CUSTOM_LIKERT_SCALE : prevState.scale
    }), () => {
      this.props.dispatchUpdateItem(this.state.itemIndex, {
        questionGroup: {
          responseOptions: responseOptions
            .map((option, idx) => ({ ...option, value: scaleStartValue + idx }))
        }
      });
    });
  }

  onClickAddValue() {
    const { responseOptions } = this.props.item.questionGroup;
    const nextValue = responseOptions.length
      ? responseOptions[responseOptions.length - 1].value + 1
      : this.state.scaleStartValue;

    this.setState({ openValueIndex: this.props.item.questionGroup.responseOptions.length });
    this.props.dispatchUpdateItem(this.state.itemIndex, {
      questionGroup: {
        responseOptions:
          [...this.props.item.questionGroup.responseOptions, { value: nextValue, text: {} }]
      }
    });
  }

  onClickEditValue(index) {
    return () => this.setState({ openValueIndex: index });
  }

  onClickRemoveValue() {
    this.setState({ openValueIndex: this.props.item.questionGroup.responseOptions.length - 2 });
    this.props.dispatchUpdateItem(this.state.itemIndex, {
      questionGroup: {
        responseOptions: this.props.item.questionGroup.responseOptions
          .slice(0, this.props.item.questionGroup.responseOptions.length - 1)
      }
    });
  }

  onClickAddQuestion() {
    const newQuestion = {
      type: questionTypeValues.LIKERT,
      required: false,
      code: this.props.nextFreeQuestionCode(),
      text: {}
    };
    this.setState({
      openQuestionIndex: this.props.item.questionGroup.questions.length
    });
    this.props.dispatchUpdateItem(this.state.itemIndex, {
      questionGroup: {
        questions: [...this.props.item.questionGroup.questions, newQuestion]
      }
    });
  }

  onClickEditQuestion(index) {
    return () => {
      this.setState({
        openQuestionIndex: index
      });
    };
  }

  onChangeQuestionText(index) {
    return (lang) => (event) => {
      const questions = assignArrayItemInObject(this.props.item.questionGroup.questions,
        index, { text: { [lang]: event.target.value } });
      this.props.dispatchUpdateItem(this.state.itemIndex, {
        questionGroup: { questions }
      });
    };
  }

  onChangeQuestionProperty(index) {
    return (event) => {
      const {
        dispatchUpdateItem, dispatchUpdateSchemaPart, surveySchemaPart, item
      } = this.props;

      const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
      const questions = assignArrayItemInObject(item.questionGroup.questions, index,
        { [event.target.name]: value });

      if (event.target.name === 'code') {
        const prevCode = item.questionGroup.questions[index].code;
        let variableChanged = false;
        const variables = surveySchemaPart.variables.map((v) => {
          const questionIndex = findIndex(v.questions, (q) => q.code === prevCode);
          if (questionIndex > -1) {
            variableChanged = true;

            return {
              ...v,
              questions: assignArrayItemInObject(v.questions, questionIndex, { code: value })
            };
          }

          return v;
        });
        if (variableChanged) {
          dispatchUpdateSchemaPart({ variables });
        }
      }

      dispatchUpdateItem(this.state.itemIndex, {
        questionGroup: { questions }
      });
    };
  }

  onRemoveQuestion(index) {
    return () => {
      const questions = this.props.item.questionGroup.questions.filter((q, idx) => idx !== index);
      this.props.dispatchUpdateItem(this.state.itemIndex, {
        questionGroup: { questions }
      });
      this.setState({
        openQuestionIndex: Math.max(index - 1, 0)
      });
    };
  }

  setLikertScale(scale) {
    const responseOptions = scale === CUSTOM_LIKERT_SCALE
      ? buildResponseOptions(2, false) : buildResponseOptions(Number(scale), true);
    this.props.dispatchUpdateItem(this.state.itemIndex, { questionGroup: { responseOptions } });
    this.setState((prevState) => ({
      scale,
      openValueIndex: 0,
      scaleStartValue: scale === CUSTOM_LIKERT_SCALE
        ? prevState.scaleStartValue
        : SCALE_DEFAULT_START_VALUE
    }));
  }

  isNewItem() {
    return !this.props.item.id
      && !this.props.item.questionGroup.responseOptions.length;
  }

  renderLocalizedTextInputs(name, values = {}, onChange) {
    const t = this.props.translate;
    return AVAILABLE_LANGUAGES.map((lang) => (
      <div key={lang} className={`formInput ${itemsStyles.elementInput}`}>
        <input
          type="text"
          name={name}
          value={values[lang] || ''}
          placeholder={t(`inLanguage.${lang}`)}
          onChange={onChange(lang)}
        />
      </div>
    ));
  }

  renderActions() {
    const t = this.props.translate;
    const constantScaleOptions = [5, 6, 7].map((scale) => (
      <option key={scale} value={scale}>
        {t('surveyParts.itemTitles.likert')} {scale}
      </option>
    ));

    return (
      <div className={editorStyles.itemActions}>
        <div className={editorStyles.headerInput}>
          <select
            className={editorStyles.dropdown}
            value={this.state.scale}
            onChange={this.onLikertScaleChange}
            data-cy="survey-schema-part-likert-scale-select"
            disabled={this.props.lockedFromEditing}
          >
            {constantScaleOptions}
            <option value="custom">
              {t('surveyParts.itemTitles.customLikert')}
            </option>
          </select>
        </div>
        <button
          type="button"
          className="actionButton icon--trash iconRemove"
          onClick={this.props.onRemove}
          title={t('common.remove')}
          disabled={this.props.lockedFromEditing}
        />
      </div>
    );
  }

  renderResponseOption(option, index) {
    const { translate: t, currentLanguage: lang } = this.props;
    const lastCustomOption = this.state.scale === CUSTOM_LIKERT_SCALE
      && this.props.item.questionGroup.responseOptions.length - 1 === index;

    if (this.state.scale === CUSTOM_LIKERT_SCALE
      && this.props.item.questionGroup.responseOptions.length > 3
      && this.state.openValueIndex !== index) {
      return (
        <li key={option.value} className={`${styles.collapsed} ${styles.contentContainer}`}>
          <div className={styles.scaleCollapsed}>{index + 1}.</div>
          <span className={`${styles.collapsedPreview} ${styles.textContainer}`}>
            {getTranslation(option.text, lang)}
          </span>
          <button
            type="button"
            className={`${styles.editButton} icon--edit-event`}
            onClick={this.onClickEditValue(index)}
            title={t('common.edit')}
          />
        </li>
      );
    }
    return (
      <li className={styles.contentContainer} key={option.value}>
        <div className={styles.scale}>{index + 1}.</div>
        <div className={styles.textContainer}>
          <div className={`${styles.value}`}>
            <p><Translate id="common.value" />: {option.value}</p>
            {lastCustomOption
              ? (
                <button
                  type="button"
                  className="actionButton icon--trash iconRemove"
                  onClick={this.onClickRemoveValue}
                  title={t('common.remove')}
                />
              ) : null}
          </div>
          {this.renderLocalizedTextInputs('text', option.text,
            this.onChangeResponseOptionText(index))}
        </div>
      </li>
    );
  }

  renderQuestion(question, index) {
    const t = this.props.translate;
    const labelSuffix = `${this.state.itemIndex}_${index}`;

    if (index !== this.state.openQuestionIndex) {
      return (
        <li key={question.code} className={styles.collapsed}>
          <span className={styles.collapsedPreview}>
            {getTranslation(question.text, this.props.currentLanguage)}
          </span>
          <button
            type="button"
            className={`${styles.editButton} icon--edit-event`}
            onClick={this.onClickEditQuestion(index)}
            title={t('common.edit')}
          />
        </li>
      );
    }

    return (
      <li key={`i${index}`}>
        <div className={editorStyles.itemActions}>
          <button
            type="button"
            className={`${styles.removeButton} actionButton icon--trash iconRemove`}
            onClick={this.onRemoveQuestion(index)}
            title={t('common.remove')}
            disabled={this.props.lockedFromEditing}
          />
        </div>
        {this.renderLocalizedTextInputs('question', question.text, this.onChangeQuestionText(index))}
        <div className={styles.questionActions}>
          <div className={`formItem ${editorStyles.headerInput} ${styles.bottomInput}`}>
            <input
              name="required"
              id={`required_${labelSuffix}`}
              type="checkbox"
              checked={question.required}
              onChange={this.onChangeQuestionProperty(index)}
              disabled={this.props.lockedFromEditing}
            />
            <label className={editorStyles.checkbox} htmlFor={`required_${labelSuffix}`}>
              <Translate id="surveyParts.required" />
            </label>
          </div>
          <div className={`formItem ${editorStyles.headerInput} ${styles.bottomInput}`}>
            <label className={editorStyles.textInput} htmlFor={`code_${labelSuffix}`}>
              <Translate id="surveyParts.identifier" />
            </label>
            <input
              id={`code_${labelSuffix}`}
              name="code"
              type="text"
              value={question.code}
              onChange={this.onChangeQuestionProperty(index)}
              disabled={this.props.lockedFromEditing}
            />
          </div>
        </div>
      </li>
    );
  }

  render() {
    const { item: { questionGroup }, lockedFromEditing } = this.props;
    const { scale, scaleStartValue } = this.state;

    return (
      <div>
        {this.renderActions()}
        <div className={editorStyles.contentContainer}>
          <div className={editorStyles.contentColumn}>
            <h2 className={editorStyles.columnTitle}><Translate id="surveyParts.likert.questions" /></h2>
            <ul>{questionGroup.questions.map(this.renderQuestion)}</ul>
            {!lockedFromEditing
              && (
              <button
                type="button"
                className="button--action icon--plus"
                onClick={this.onClickAddQuestion}
                data-cy="survey-schema-part-add-likert-question-button"
              >
                <Translate id="surveyParts.likert.addQuestion" />
              </button>
              )}
          </div>
          <div className={editorStyles.contentColumn}>
            <h2 className={editorStyles.columnTitle}><Translate id="surveyParts.likert.scale" /></h2>

            <div className={styles.scaleStartValue}>
              <label className={editorStyles.textInput} htmlFor="scale_start_value">
                <Translate id="surveyParts.likert.scaleStartValue" />:
              </label>
              <select
                className={editorStyles.dropdown}
                id="scale_start_value"
                value={scaleStartValue}
                onChange={this.onLikertScaleStartValueChange}
                disabled={lockedFromEditing}
                data-cy="survey-schema-part-likert-scale-start-select"
              >
                {range(SCALE_MIN_START_VALUE, SCALE_MAX_START_VALUE + 1).map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <ul>{questionGroup.responseOptions.map(this.renderResponseOption)}</ul>
            {scale === CUSTOM_LIKERT_SCALE && !lockedFromEditing
              ? (
                <button type="button" className="button--action icon--plus" onClick={this.onClickAddValue}>
                  <Translate id="surveyParts.likert.addValue" />
                </button>
              ) : null}
          </div>
        </div>
      </div>
    );
  }
}

QuestionGroup.propTypes = {
  surveySchemaPart: SurveySchemaPartEditorType.isRequired,
  item: SurveySchemaPartItemEditorType.isRequired,
  onRemove: func.isRequired,
  nextFreeQuestionCode: func.isRequired,
  dispatchUpdateSchemaPart: func.isRequired,
  dispatchUpdateItem: func.isRequired,
  lockedFromEditing: bool.isRequired,
  ...langType
};

const mapStateToProps = ({ surveySchemaPart }) => ({
  surveySchemaPart: surveySchemaPart.surveySchemaPartOptions
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUpdateSchemaPart: (options) =>
    dispatch(updateSurveySchemaPartOptionsAction(options)),
  dispatchUpdateItem: (index, item) =>
    dispatch(updateSchemaPartItemAction(index, item))
});

export default withLang(connect(mapStateToProps, mapDispatchToProps)(QuestionGroup));
